import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import weekday from "dayjs/plugin/weekday";
import isToday from "dayjs/plugin/isToday";
import duration from "dayjs/plugin/duration";
import { LinearGradient } from "react-text-gradients";
import type { Linear } from "react-text-gradients/lib/types/types";

dayjs.extend(weekday);
dayjs.extend(isToday);
dayjs.extend(duration);
dayjs.locale("ko");

type WeddingCalendarProps = {
  weddingDateKR: string;      // "결혼식까지" 같은 문구
  weddingDate: string;        // "2025-11-09" 또는 "2025-11-09 15:10"
  backgroundColor?: string;
  textColor?: string;
  gradient?: Linear | null;
};

const WeddingCalendar = ({
  weddingDateKR,
  weddingDate,
  backgroundColor = "bg-white",
  textColor = "text-red-500",
  gradient = null,
}: WeddingCalendarProps) => {
  // "시간까지 들어왔는지" 간단 체크(예: 2025-11-09 15:10)
  const hasTime = /\d{2}:\d{2}/.test(weddingDate);

  // 날짜만 들어오면 해당 날짜의 00:00, 시간이 있으면 그 시각 그대로
  const target = useMemo(() => {
    if (hasTime) {
      // "YYYY-MM-DD HH:mm" 우선 시도, 실패 시 dayjs 기본 파서로
      const parsed = dayjs(weddingDate, "YYYY-MM-DD HH:mm", true);
      return parsed.isValid() ? parsed : dayjs(weddingDate);
    } else {
      // 날짜만: 자정 기준
      const parsed = dayjs(weddingDate, "YYYY-MM-DD", true);
      return (parsed.isValid() ? parsed : dayjs(weddingDate)).startOf("day");
    }
  }, [weddingDate, hasTime]);

  const [now, setNow] = useState(dayjs());
  useEffect(() => {
    const t = setInterval(() => setNow(dayjs()), 1000);
    return () => clearInterval(t);
  }, []);

  // 달력용 범위
  const startOfMonth = target.startOf("month");
  const endOfMonth = target.endOf("month");

  const days: dayjs.Dayjs[] = [];
  let current = startOfMonth.startOf("week");
  while (current.isBefore(endOfMonth.endOf("week"))) {
    days.push(current);
    current = current.add(1, "day");
  }

  // 카운트다운
  const diffMs = target.valueOf() - now.valueOf();
  const daysLeft = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hoursLeft = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutesLeft = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const secondsLeft = Math.floor((diffMs % (1000 * 60)) / 1000);

  return (
    <div className={`bg-white p-6 text-center rounded-md mx-8 mt-8 font-gowun`}>
      <h2 className="text-xl font-semibold mb-4">
        {target.format("YYYY년 M월 D일")}
      </h2>

      <div className="grid grid-cols-7 gap-2 mb-4 text-sm font-medium">
        {["일", "월", "화", "수", "목", "금", "토"].map((d) =>
          gradient ? (
            <LinearGradient key={d} gradient={gradient}>
              {d}
            </LinearGradient>
          ) : (
            <div key={d} className={`text-sm font-medium ${textColor}`}>{d}</div>
          )
        )}

        {days.map((day, idx) => {
          const isCurrentMonth = day.month() === target.month();
          const isWeddingDay = day.isSame(target, "day");
          return (
            <div
              key={idx}
              className={`text-sm h-10 flex items-center justify-center rounded-full
                ${isWeddingDay ? `${backgroundColor} text-white font-bold` : ""}
                ${!isCurrentMonth ? "text-gray-300" : ""}
              `}
            >
              {day.date()}
            </div>
          );
        })}
      </div>
      
{!(
  weddingDate === "2026-03-28 17:30" &&
  backgroundColor === "bg-parentText"
) && (
  <>
    {gradient ? (
      <LinearGradient gradient={gradient!}>
        {weddingDateKR}까지
      </LinearGradient>
    ) : (
      <div className={`mt-4 text-lg font-medium ${textColor}`}>
        {weddingDateKR}까지
      </div>
    )}

    <div className="text-sm mt-1">
      {now.isSame(target, "day") ? (
        "오늘입니다!"
      ) : now.isBefore(target) ? (
        <>
          {daysLeft}일 {hoursLeft}시간 {minutesLeft}분 {secondsLeft}초 남았습니다.
        </>
      ) : (
        <>
          {Math.abs(daysLeft)}일 {Math.abs(hoursLeft)}시간 {Math.abs(minutesLeft)}분 {Math.abs(secondsLeft)}초 지났습니다.
        </>
      )}
    </div>
  </>
)}
    </div>
  );
};

export default WeddingCalendar;